import React, { useEffect, useState, useRef } from 'react';
import './App.css';
import { BrandEntry } from './types';
import { loadBrandConfig } from './config/loadBrandConfig';
import { initPixel } from './pixel/metaPixel';
import { initGTM } from './pixel/gtm';
import LandingPage from './components/LandingPage';
import ErrorPage from './components/ErrorPage';

type AppState = 'loading' | 'ready' | 'error';

function App() {
  const [state, setState] = useState<AppState>('loading');
  const [brand, setBrand] = useState<BrandEntry | null>(null);
  const pixelInitRef = useRef(false);
  const gtmInitRef = useRef(false);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);

    let stateOverrides: Partial<BrandEntry> = {};
    const stateParam = params.get('state');
    if (stateParam) {
      try {
        stateOverrides = JSON.parse(atob(stateParam));
      } catch {
        // invalid base64/JSON, ignore
      }
    }

    const brandSlug = stateOverrides.slug || params.get('brand');

    function applyConfig(config: BrandEntry) {
      // Merge: state overrides on top of brand config
      const merged = { ...config, ...stateOverrides };
      setBrand(merged);
      setState('ready');

      const pixelOff = params.get('pixelOff') || merged.pixelOff || '';
      const pixelDisabled = pixelOff === '1' || pixelOff === 'true';
      const pixelId = params.get('pixelId') || merged.pixelId;
      if (pixelId && !pixelDisabled && !pixelInitRef.current) {
        pixelInitRef.current = true;
        initPixel(pixelId);
      }

      const gtmId = params.get('gtmId') || merged.gtmId;
      if (gtmId && !gtmInitRef.current) {
        gtmInitRef.current = true;
        initGTM(gtmId);
      }
    }

    if (brandSlug) {
      loadBrandConfig(brandSlug).then((config) => {
        if (config) {
          applyConfig(config);
        } else if (stateOverrides.redirectUrl) {
          // No brand in DB but state has enough data to render
          applyConfig(stateOverrides as BrandEntry);
        } else {
          setState('error');
        }
      });
    } else if (stateOverrides.redirectUrl) {
      // state-only mode, no slug needed if state has full data
      applyConfig(stateOverrides as BrandEntry);
    } else {
      setState('error');
    }
  }, []);

  if (state === 'loading') {
    return null;
  }

  if (state === 'error' || !brand) {
    return <ErrorPage />;
  }

  return <LandingPage config={brand} redirectUrl={brand.redirectUrl} />;
}

export default App;
